import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ReactComponent as NewChatIllustration } from '@assets/images/chat/new-chat.svg';
import { ReactComponent as RoomsRemoteSearchArrowIllustration } from '@assets/images/chat/rooms-remote-search-arrow.svg';

import {
    useCurrentStudyChatStatus,
    useCurrentStudyRooms,
    useCurrentStudyRoomsOrder,
    useCurrentStudyUsersData,
    useCurrentUserId,
    useDynamicMethods,
    useIsCreatingARoom,
    useIsRoomsSearchRemote,
    useMethods,
    useRemoteSearchRooms,
    useRoomsSearchKeyword,
} from '$app-web/components/chat/state/chat-client.state';
import Avatar from '$app-web/ui-kit/components/avatar';
import Badge from '$app-web/ui-kit/components/badge';
import ButtonBase from '$app-web/ui-kit/components/button-base';
import ChatIconButton from '$app-web/ui-kit/components/chat-icon-button';
import { appDateTime } from '$app-web/utils';

import { CATEGORY_TYPES } from '../../constants';
import { getChatRoomAvatar, getChatRoomTitle, getEventMessage } from '../../utils/chat-client.utils';
import classes from './styles.module.scss';

import { FontAwesomeIcon } from '$common/fontawesome-pro/react-fontawesome';

const RoomsList = (props) => {
    const { category, currentRoomId, roomOnClick, showArchiveds } = props;

    const { t } = useTranslation();

    const chatStatus = useCurrentStudyChatStatus();
    const roomsOrder = useCurrentStudyRoomsOrder();
    const rooms = useCurrentStudyRooms();
    const roomsSearchKeyword = useRoomsSearchKeyword();
    const dynamicMethods = useDynamicMethods();
    const currentStudyUsersData = useCurrentStudyUsersData();
    const currentUserId = useCurrentUserId();
    const isRoomsSearchRemote = useIsRoomsSearchRemote();
    const remoteSearchRooms = useRemoteSearchRooms();
    const isCreatingARoom = useIsCreatingARoom();
    const methods = useMethods();

    const typingStatus = useCallback(
        (roomId) => {
            const { membersTyping, studyId } = rooms[roomId];

            if (membersTyping?.length > 0) {
                const firstMemberLabel = dynamicMethods.getStudyUserDataByMatrixId(studyId, membersTyping[0])?.label || '';
                return membersTyping.length === 1
                    ? t('chat_typing_status_single', [firstMemberLabel])
                    : t('chat_typing_status_multiple', [firstMemberLabel, membersTyping.length - 1]);
            }

            return null;
        },
        [rooms, dynamicMethods, t]
    );

    // The way we filter the rooms based on the search keyword won't be performant with lots of rooms.
    // But currently, we won't need that performance since the number of rooms in target studies is less than 400.
    const filteredRoomsOrder = useMemo(
        () =>
            roomsOrder?.filter(
                (roomId) =>
                    (category === CATEGORY_TYPES.ALL ||
                        (category === CATEGORY_TYPES.DIRECT ? rooms[roomId].direct : !rooms[roomId].direct)) &&
                    (!roomsSearchKeyword || rooms[roomId].title.toLowerCase().includes(roomsSearchKeyword.toLowerCase())) &&
                    (showArchiveds ? rooms[roomId].isArchived : !rooms[roomId].isArchived)
            ) || [],
        [category, rooms, roomsOrder, roomsSearchKeyword, showArchiveds]
    );

    if (roomsOrder === undefined || rooms === undefined || !chatStatus.isReady) {
        return null;
    }

    return (
        <div className={clsx('flex column nowrap scrollable', classes.root)}>
            {!isRoomsSearchRemote
                ? filteredRoomsOrder.map((roomId) => (
                      <ButtonBase
                          key={roomId}
                          className={clsx('item', classes.roomWrapper, roomId === currentRoomId && classes.current)}
                          onClick={() => roomOnClick(roomId)}
                      >
                          <div className="flex nowrap">
                              <div className={clsx('flex auto-width content-center item', classes.roomAvatarWrapper)}>
                                  <Avatar
                                      colorValue={rooms[roomId].avatar.color}
                                      label={rooms[roomId].avatar.label}
                                      online={
                                          rooms[roomId].direct
                                              ? currentStudyUsersData[rooms[roomId].otherMemberId]?.isOnline
                                              : undefined
                                      }
                                      size="lg"
                                      src={getChatRoomAvatar(rooms[roomId], currentStudyUsersData).url}
                                  />
                              </div>
                              <div className="item xs-fill zero-min-width">
                                  <div className={clsx('typo-old subtitle1 nowrap', classes.roomTitle)}>
                                      {!rooms[roomId].direct && (
                                          <FontAwesomeIcon icon={['fas', 'user-friends']} className={classes.groupRoomIcon} />
                                      )}
                                      {getChatRoomTitle(rooms[roomId], currentStudyUsersData, currentUserId, t) || '\u00A0'}
                                  </div>
                                  <div className={clsx('typo-old body2 nowrap', classes.roomLastMessage)}>
                                      {typingStatus(roomId) ||
                                          (rooms[roomId].timeline.events.length > 0 &&
                                              getEventMessage(
                                                  rooms[roomId].timeline.events[rooms[roomId].timeline.events.length - 1],
                                                  t
                                              ))}
                                  </div>
                              </div>
                              <div className={clsx('item', classes.roomMetadataWrapper)}>
                                  <div className="typo-old body3 nowrap">
                                      {rooms[roomId].timeline.events.length > 0 &&
                                          rooms[roomId].timeline.events[rooms[roomId].timeline.events.length - 1].getTs() > 0 &&
                                          appDateTime(
                                              rooms[roomId].timeline.events[rooms[roomId].timeline.events.length - 1].getTs()
                                          ).toNow(true)}
                                  </div>
                                  <Badge
                                      className={classes.unreadMessagesCount}
                                      content={rooms[roomId].unreadMessagesCount}
                                      color="green"
                                      max={9}
                                      invisible={rooms[roomId].unreadMessagesCount < 1}
                                  />
                              </div>
                          </div>
                      </ButtonBase>
                  ))
                : remoteSearchRooms.map((room) => (
                      <ButtonBase
                          key={room.alias}
                          className={clsx('item', classes.roomWrapper)}
                          onClick={() => dynamicMethods.createOrOpenRoom(room.alias)}
                          disabled={isCreatingARoom}
                      >
                          <div className="flex nowrap">
                              <div className={clsx('flex auto-width content-center item', classes.roomAvatarWrapper)}>
                                  <Avatar
                                      colorValue={room.avatar.color}
                                      label={room.avatar.label}
                                      size="lg"
                                      src={room.avatar.url}
                                  />
                              </div>
                              <div className="item xs-fill zero-min-width">
                                  <div className={clsx('typo-old subtitle1 nowrap', classes.roomTitle)}>
                                      {!room.direct && (
                                          <FontAwesomeIcon icon={['fas', 'user-friends']} className={classes.groupRoomIcon} />
                                      )}
                                      {room.label || '\u00A0'}
                                  </div>
                              </div>
                          </div>
                      </ButtonBase>
                  ))}
            {!showArchiveds && !isRoomsSearchRemote && !roomsSearchKeyword && filteredRoomsOrder.length === 0 && (
                <>
                    <div className="flex justify-center items-center full-height">
                        <div className="item text-center">
                            <NewChatIllustration />
                            <div className="typo-old subtitle1 text-center">{t('your_chats_will_appear_here')}</div>
                            <div className="typo-old body2 text-center">{t('press_plus_to_start_a_new_chat')}</div>
                        </div>
                    </div>
                    <RoomsRemoteSearchArrowIllustration className={classes.fabArrow} />
                </>
            )}
            {!isRoomsSearchRemote && (
                <ChatIconButton
                    icon={['fal', 'plus']}
                    className={classes.fab}
                    color="secondary"
                    onClick={() => {
                        methods.setIsRoomsSearchRemote(true);
                    }}
                    fill
                />
            )}
        </div>
    );
};

RoomsList.defaultProps = {
    currentRoomId: undefined,
};

RoomsList.propTypes = {
    category: PropTypes.oneOf(Object.values(CATEGORY_TYPES)).isRequired,
    currentRoomId: PropTypes.string,
    roomOnClick: PropTypes.func.isRequired,
    showArchiveds: PropTypes.bool.isRequired,
};

export default RoomsList;
